import React, { useState, useEffect, useRef } from 'react';
import fetchMRNProjects from '../MaterialRequests/Helpers/fetchProjects';
import writeBuildProjectId from './writeBuildProjectId';

const InventoryDropdown = ({projectId}) => {
  
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);
  const [showButton, setShowButton] = useState(true);
  const [projects, setProjects] = useState([]);
  const [linkedProject, setLinkedProject] = useState(null);
  const [loading, setLoading] = useState(false);

  // Enhanced ID comparison - handles type conversion, whitespace, and null/undefined
  const compareIds = (id1, id2) => {
    // Handle nullish values
    if (id1 == null || id2 == null) return false;
    
    // Convert to strings, trim whitespace, and compare
    return String(id1).trim() === String(id2).trim();
  };

  // Debug function to help identify issues with ID matching
  const logIdComparison = (project) => {
    console.log("Comparing IDs:", {
      projectId: projectId,
      projectIdType: typeof projectId,
      buildProjectId: project.buildProjectId,
      buildProjectIdType: typeof project.buildProjectId,
      isMatch: compareIds(project.buildProjectId, projectId)
    });
  };

  // Check if there's a linked project when component mounts
  const checkLinkedProject = async () => {
    setLoading(true);
    try {
      const data = await fetchMRNProjects();
      
      if (!data || !data[0]) {
        setLoading(false);
        return;
      }
      
      // Enhanced debugging
      console.log("ProjectId received:", projectId, typeof projectId);
      console.log("All projects:", data[0]);
      
      // Find linked project with robust comparison
      let foundLinkedProject = null;
      for (const project of data[0]) {
        logIdComparison(project);
        if (compareIds(project.buildProjectId, projectId)) {
          foundLinkedProject = project;
          break;
        }
      }
      
      // Store all projects
      setProjects(data[0]);
      
      // Update UI based on whether we found a linked project
      if (foundLinkedProject) {
        console.log("Found linked project:", foundLinkedProject);
        setShowButton(false);
        setLinkedProject(foundLinkedProject);
      } else {
        console.log("No linked project found for projectId:", projectId);
        setShowButton(true);
        setLinkedProject(null);
      }
      
    } catch (error) {
      console.error("Error checking linked project:", error);
    } finally {
      setLoading(false);
    }
  };

  // Ensure the check runs when projectId changes
  useEffect(() => {
    console.log("ProjectId changed to:", projectId);
    checkLinkedProject();
  }, [projectId]);

  const handleOptionSelect = (value) => {
    console.log("Selected project:", value);
    setSelectedOption(value);
    writeBuildProjectId(value, projectId);  // Ensure correct parameter order
    setLinkedProject(value);
    setShowButton(false);
    setIsOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Executive-level UI styling with optimized spacing
  const styles = {
    container: {
      position: 'relative',
      display: 'inline-block',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    },
    linkedStatus: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'rgba(240, 253, 244, 1)',
      padding: '10px 14px',  // Reduced padding
      borderRadius: '8px',
      border: '1px solid rgba(22, 163, 74, 0.2)',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },
    linkedLabel: {
      color: '#166534',
      fontWeight: 600,
      marginRight: '10px',  // Reduced margin
      fontSize: '14px'
    },
    linkedValue: {
      color: '#166534',
      fontWeight: 500,
      fontSize: '14px',
      flexGrow: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '180px'  // Add max width for better control
    },
    changeButton: {
      backgroundColor: 'white',
      color: '#166534',
      padding: '6px 12px',  // Reduced padding
      borderRadius: '6px',
      border: '1px solid #16a34a',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      marginLeft: '8px'  // Added margin left for spacing from text
    },
    linkButton: {
      padding: '10px 18px',  // Adjusted padding
      fontSize: '14px',
      fontWeight: 500,
      backgroundColor: '#16a34a',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'
    },
    dropdownList: {
      position: 'absolute',
      top: 'calc(100% + 8px)',
      left: 0,
      width: '320px',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      borderRadius: '12px',
      zIndex: 50,
      maxHeight: '320px',
      overflowY: 'auto',
      padding: '6px',
      margin: 0,
      listStyle: 'none'
    },
    loadingItem: {
      padding: '12px',  // Reduced padding
      color: '#6b7280',
      textAlign: 'center',
      fontSize: '14px',
      borderRadius: '8px'
    },
    emptyItem: {
      padding: '12px',  // Reduced padding
      color: '#6b7280',
      textAlign: 'center',
      fontSize: '14px',
      borderRadius: '8px'
    },
    dropdownItem: (isLinked) => ({
      padding: '10px 14px',  // Reduced padding
      cursor: 'pointer',
      borderRadius: '8px',
      marginBottom: '3px',  // Reduced margin
      backgroundColor: isLinked ? 'rgba(240, 253, 244, 1)' : 'white',
      border: isLinked ? '1px solid rgba(22, 163, 74, 0.2)' : '1px solid transparent',
      fontWeight: isLinked ? 600 : 400,
      fontSize: '14px',
      transition: 'all 0.15s ease',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }),
    linkedBadge: {
      color: '#16a34a',
      fontSize: '12px',
      fontWeight: 600,
      padding: '2px 8px',
      backgroundColor: 'rgba(22, 163, 74, 0.1)',
      borderRadius: '100px',
      display: 'inline-block',
      whiteSpace: 'nowrap'
    },
    projectName: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '200px'  // Add max width for better control
    }
  };

  return (
    <div ref={dropdownRef} style={styles.container}>
      {loading ? (
        <div style={{ 
          padding: '10px 16px',  // Reduced padding
          fontSize: '14px',
          color: '#6b7280',
          backgroundColor: '#f9fafb', 
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          Checking inventory link...
        </div>
      ) : linkedProject ? (
        <div style={styles.linkedStatus}>
          <span style={styles.linkedLabel}>
            Linked to:
          </span>
          <span style={styles.linkedValue} title={linkedProject.projectName}>
            {linkedProject.projectName}
          </span>
          <button
            onClick={() => setIsOpen(!isOpen)}
            style={styles.changeButton}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(22, 163, 74, 0.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            Change
          </button>
        </div>
      ) : (
        showButton && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            style={styles.linkButton}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#15803d';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#16a34a';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)';
            }}
          >
            Link to Inventory
          </button>
        )
      )}
      
      {isOpen && (
        <ul style={styles.dropdownList}>
          <div style={{ padding: '8px 12px 4px', fontSize: '12px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Select Project
          </div>
          {loading ? (
            <li style={styles.loadingItem}>
              Loading projects...
            </li>
          ) : projects.length > 0 ? (
            projects.map((option, index) => {
              const isLinked = compareIds(option.buildProjectId, projectId);
              return (
                <li
                  key={index}
                  onClick={() => handleOptionSelect(option)}
                  style={styles.dropdownItem(isLinked)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = isLinked ? 'rgba(220, 252, 231, 1)' : '#f9fafb';
                    e.currentTarget.style.border = isLinked ? '1px solid rgba(22, 163, 74, 0.3)' : '1px solid #e5e7eb';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = isLinked ? 'rgba(240, 253, 244, 1)' : 'white';
                    e.currentTarget.style.border = isLinked ? '1px solid rgba(22, 163, 74, 0.2)' : '1px solid transparent';
                  }}
                >
                  <div style={styles.projectName} title={option.projectName}>
                    {option.projectName}
                  </div>
                  {isLinked && (
                    <span style={styles.linkedBadge}>
                      Linked
                    </span>
                  )}
                </li>
              );
            })
          ) : (
            <li style={styles.emptyItem}>
              No projects available
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default InventoryDropdown;